export const SOURCE_CHANGES_REFRESH_SUPERSEDED = Symbol("source-changes-refresh-superseded");

export function createSourceChangesRefreshCoordinator() {
  const inFlightByScope = new Map();

  return function coordinateSourceChangesRefresh({
    background = false,
    forceFresh = false,
    refreshKey = "",
    refresh,
    reportUnavailable = false,
    scopeKey = "",
    onDiagnostic,
  } = {}) {
    const normalizedScopeKey = typeof scopeKey === "string" ? scopeKey.trim() : "";
    if (!normalizedScopeKey) {
      throw new Error("Un scope est requis pour rafraîchir les changements source.");
    }
    if (typeof refresh !== "function") {
      throw new Error("Une fonction de rafraîchissement est requise.");
    }

    const existingEntry = inFlightByScope.get(normalizedScopeKey);
    if (existingEntry) {
      if (typeof onDiagnostic === "function") existingEntry.observers.add(onDiagnostic);
      // A post-mutation check accepts an absent evening dispatch; a coalesced
      // ordinary read must not turn that expected 404 back into a failure.
      existingEntry.reportUnavailable = existingEntry.reportUnavailable || reportUnavailable === true;
      // The same operation's final read can be requested by several local consumers.
      // A different mutation (or an ordinary read) still requires the trailing read.
      if (refreshKey && (existingEntry.refreshKey === refreshKey || existingEntry.trailingKey === refreshKey)) {
        return existingEntry.promise;
      }
      existingEntry.forceFresh = existingEntry.forceFresh || forceFresh === true;
      existingEntry.hasTrailingRefresh = true;
      existingEntry.trailingBackground = existingEntry.trailingBackground && background === true;
      existingEntry.trailingRefresh = refresh;
      existingEntry.trailingKey = refreshKey;
      return existingEntry.promise;
    }

    const entry = {
      forceFresh: forceFresh === true,
      reportUnavailable: reportUnavailable === true,
      refreshKey,
      trailingKey: "",
      hasTrailingRefresh: false,
      promise: null,
      trailingBackground: true,
      trailingRefresh: null,
      observers: new Set(typeof onDiagnostic === "function" ? [onDiagnostic] : []),
    };
    // Count coordinated verification passes, not internal metadata HTTP requests.
    const metrics = { read_count: 0, superseded_count: 0, retry_count: 0 };
    const passId = Symbol(normalizedScopeKey);
    const notify = (result_code) => {
      for (const observer of entry.observers) {
        try { observer({ ...metrics, result_code, scope_key: normalizedScopeKey, pass_id: passId }); } catch { /* Diagnostics cannot change a read. */ }
      }
    };
    entry.promise = (async () => {
      let nextBackground = background === true;
      let nextRefresh = refresh;
      let result = null;
      let supersededRetries = 0;

      while (nextRefresh) {
        entry.hasTrailingRefresh = false;
        entry.trailingBackground = true;
        entry.trailingRefresh = null;
        // Ordinary reads share the post-mutation scope and must not downgrade
        // its fresh requirement before the final coalesced request completes.
        metrics.read_count += 1;
        result = await nextRefresh({
          background: nextBackground, forceFresh: entry.forceFresh, reportUnavailable: entry.reportUnavailable,
        });
        if (result === SOURCE_CHANGES_REFRESH_SUPERSEDED) metrics.superseded_count += 1;

        if (!entry.hasTrailingRefresh) {
          // A bootstrap can invalidate a successful final GET without queuing
          // another read. Fetch once more instead of reporting a false failure.
          if (result === SOURCE_CHANGES_REFRESH_SUPERSEDED && supersededRetries < 1) {
            supersededRetries += 1;
            metrics.retry_count += 1;
            entry.forceFresh = true;
            continue;
          }
          break;
        }
        nextBackground = entry.trailingBackground;
        nextRefresh = entry.trailingRefresh;
        entry.refreshKey = entry.trailingKey;
      }

      notify(result === SOURCE_CHANGES_REFRESH_SUPERSEDED ? "superseded" : result ? "confirmed" : "unconfirmed");
      return result === SOURCE_CHANGES_REFRESH_SUPERSEDED ? null : result;
    })().catch((error) => {
      notify("request_failed");
      throw error;
    }).finally(() => {
      if (inFlightByScope.get(normalizedScopeKey) === entry) {
        inFlightByScope.delete(normalizedScopeKey);
      }
    });
    inFlightByScope.set(normalizedScopeKey, entry);
    return entry.promise;
  };
}

export function createSourceChangeMetadataSyncCoordinator() {
  const inFlightByKey = new Map();
  let queueTail = Promise.resolve();

  return function coordinateSourceChangeMetadataSync({
    key = "",
    request,
    shared = null,
  } = {}) {
    const normalizedKey = typeof key === "string" ? key.trim() : "";
    if (!normalizedKey) {
      throw new Error("Une clé est requise pour synchroniser les métadonnées source.");
    }
    if (typeof request !== "function") {
      throw new Error("Une requête est requise pour synchroniser les métadonnées source.");
    }

    const existingEntry = inFlightByKey.get(normalizedKey);
    if (existingEntry) {
      return existingEntry;
    }

    const entry = {
      key: normalizedKey,
      promise: null,
      shared,
    };
    entry.promise = queueTail.then(() => request());
    queueTail = entry.promise.catch(() => undefined);
    inFlightByKey.set(normalizedKey, entry);
    entry.promise.then(
      () => {
        if (inFlightByKey.get(normalizedKey) === entry) {
          inFlightByKey.delete(normalizedKey);
        }
      },
      () => {
        if (inFlightByKey.get(normalizedKey) === entry) {
          inFlightByKey.delete(normalizedKey);
        }
      },
    );
    return entry;
  };
}
